"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createDateDimension = exports.createDimension = exports.DimensionalDateDimension = exports.DimensionalDimension = void 0;
const types_js_1 = require("../types.js");
const attributes_js_1 = require("../attributes.js");
const base_js_1 = require("../base.js");
/**
 * Represents a Dimension in a Dimensional Model
 *
 * @internal
 */
class DimensionalDimension extends base_js_1.DimensionalElement {
    constructor(name, expression, attributes, dimensions, type, desc, sort, dataSource) {
        super(name, type || types_js_1.MetadataTypes.Dimension, desc, dataSource);
        this._dimensions = [];
        this._attributes = [];
        this._sort = types_js_1.Sort.None;
        this._sort = sort || types_js_1.Sort.None;
        this._expression = expression;
        this.setDimensions(dimensions || []);
        this.setAttributes(attributes);
    }
    static parseType(type) {
        switch (type) {
            case 'datetime':
            case types_js_1.MetadataTypes.DateDimension:
                return types_js_1.MetadataTypes.DateDimension;
            case 'text':
            case types_js_1.MetadataTypes.TextDimension:
                return types_js_1.MetadataTypes.TextDimension;
            case 'numeric':
            case types_js_1.MetadataTypes.NumericDimension:
                return types_js_1.MetadataTypes.NumericDimension;
        }
        return types_js_1.MetadataTypes.TextDimension;
    }
    getAttachedName(name, expression) {
        let result = name;
        // if exists fallback to expression
        const normalizedName = (0, base_js_1.normalizeName)(name);
        if (normalizedName === 'id' ||
            normalizedName === 'name' ||
            Object.getOwnPropertyDescriptor(this, normalizedName) !== undefined ||
            this[normalizedName] !== undefined) {
            result = expression;
        }
        return result;
    }
    setDimensions(dimensions) {
        this._dimensions = dimensions;
        for (let i = 0; i < dimensions.length; i++) {
            const n = this.getAttachedName(dimensions[i].name, dimensions[i].attributes[0].expression);
            this[(0, base_js_1.normalizeName)(n)] = dimensions[i];
            if (n != dimensions[i].name) {
                dimensions[i].name = n;
            }
        }
    }
    setAttributes(attributes) {
        this._attributes = attributes || [];
        for (let i = 0; i < attributes.length; i++) {
            const n = this.getAttachedName(attributes[i].name, attributes[i].expression);
            this[(0, base_js_1.normalizeName)(n)] = attributes[i];
            if (attributes[i].name != n) {
                attributes[i].name = n;
            }
            // attaching to dimension
            //this.attributes[i].dimension = this;
        }
    }
    /**
     * gets the element's ID
     */
    get id() {
        return this._expression;
    }
    /**
     * Gets the child attributes
     */
    get attributes() {
        return this._attributes;
    }
    /**
     * Gets the child dimensions
     */
    get dimensions() {
        return this._dimensions;
    }
    get expression() {
        return this._expression;
    }
    /**
     * Gets the sort definition of this instance
     *
     * @returns The Sort definition of this instance
     */
    getSort() {
        return this._sort;
    }
    /**
     * Gets a sorted {@link Dimension} with the given definition
     *
     * @param sort - Sort definition
     * @returns An instance representing the sorted {@link Dimension} of this instance
     */
    sort(sort) {
        return new DimensionalDimension(this.name, this.expression, this.attributes, this.dimensions, this.type, this.description, sort, this.dataSource);
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.expression = this.expression;
        if (this.getSort() !== undefined) {
            result.sort = this.getSort();
        }
        result.attributes = this._attributes.map((att) => att.serializable());
        result.diemsnions = this._dimensions.map((dim) => dim.serializable());
        if (this.defaultAttribute) {
            result.defaultAttribute = this.defaultAttribute.serializable();
        }
        return result;
    }
    /**
     * Gets the JAQL representation of this instance
     *
     * @param nested - defines whether the JAQL is nested within parent JAQL statement or a root JAQL element
     */
    jaql(nested) {
        if (this.defaultAttribute) {
            return this.defaultAttribute.jaql(nested);
        }
        if (this.dimensions.length > 0) {
            return this.dimensions[0].jaql(nested);
        }
        const result = {
            jaql: {
                title: this.name,
                dim: this.expression,
                datatype: (0, attributes_js_1.jaqlSimpleColumnType)(this.type),
            },
        };
        if (this._sort != types_js_1.Sort.None) {
            result.jaql.sort = this._sort == types_js_1.Sort.Ascending ? 'asc' : 'desc';
        }
        return nested === true ? result.jaql : result;
    }
}
exports.DimensionalDimension = DimensionalDimension;
/**
 * Represents a Date Dimension in a Dimensional Model
 *
 * @internal
 */
class DimensionalDateDimension extends DimensionalDimension {
    constructor(name, expression, desc, sort, dataSource) {
        super(name, expression, [], [], types_js_1.MetadataTypes.DateDimension, desc, sort, dataSource);
        this.defaultLevel = types_js_1.DateLevels.Years;
        this.Years = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.Years, expression, types_js_1.DateLevels.Years, 'yyyy', desc, sort, dataSource);
        this.Quarters = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.Quarters, expression, types_js_1.DateLevels.Quarters, 'Q yyyy', desc, sort, dataSource);
        this.Months = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.Months, expression, types_js_1.DateLevels.Months, 'yyyy-MM', desc, sort, dataSource);
        this.Weeks = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.Weeks, expression, types_js_1.DateLevels.Weeks, 'ww yyyy', desc, sort, dataSource);
        this.Days = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.Days, expression, types_js_1.DateLevels.Days, 'yyyy-MM-dd', desc, sort, dataSource);
        this.Hours = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.Hours, expression, types_js_1.DateLevels.Hours, 'yyyy-MM-dd HH:mm', desc, sort, dataSource);
        this.MinutesRoundTo30 = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.MinutesRoundTo30, expression, types_js_1.DateLevels.MinutesRoundTo30, 'yyyy-MM-dd HH:mm', desc, sort, dataSource);
        this.MinutesRoundTo15 = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.MinutesRoundTo15, expression, types_js_1.DateLevels.MinutesRoundTo15, 'yyyy-MM-dd HH:mm', desc, sort, dataSource);
        this.Minutes = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.Minutes, expression, types_js_1.DateLevels.Minutes, 'yyyy-MM-dd HH:mm', desc, sort, dataSource);
        this.Seconds = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.Seconds, expression, types_js_1.DateLevels.Seconds, 'yyyy-MM-dd HH:mm:ss', desc, sort, dataSource);
        this.AggHours = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.AggHours, expression, types_js_1.DateLevels.AggHours, 'HH:mm', desc, sort, dataSource);
        this.AggMinutesRoundTo30 = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.AggMinutesRoundTo30, expression, types_js_1.DateLevels.AggMinutesRoundTo30, 'HH:mm', desc, sort, dataSource);
        this.AggMinutesRoundTo15 = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.AggMinutesRoundTo15, expression, types_js_1.DateLevels.AggMinutesRoundTo15, 'HH:mm', desc, sort, dataSource);
        this.AggMinutesRoundTo1 = new attributes_js_1.DimensionalLevelAttribute(types_js_1.DateLevels.AggMinutesRoundTo1, expression, types_js_1.DateLevels.AggMinutesRoundTo1, 'HH:mm', desc, sort, dataSource);
        this.setAttributes([
            this.Years,
            this.Quarters,
            this.Months,
            this.Weeks,
            this.Days,
            this.Hours,
            this.MinutesRoundTo30,
            this.MinutesRoundTo15,
            this.Minutes,
            this.Seconds,
            this.AggHours,
            this.AggMinutesRoundTo30,
            this.AggMinutesRoundTo15,
            this.AggMinutesRoundTo1,
        ]);
    }
    /**
     * gets the element's ID
     */
    get id() {
        return this.expression;
    }
    setDimensions() {
        /* */
    }
    setAttributes(attributes) {
        this._attributes = attributes;
    }
    /**
     * Gets the sort definition of this instance
     *
     * @returns The Sort definition of this instance
     */
    getSort() {
        return this._sort;
    }
    /**
     * Gets a sorted {@link Dimension} with the given definition
     *
     * @param sort - Sort definition
     * @returns An instance representing the sorted {@link Dimension} of this instance
     */
    sort(sort) {
        return new DimensionalDateDimension(this.name, this.expression, this.description, sort, this.dataSource);
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        if (this.defaultLevel) {
            result.defaultLevel = this.defaultLevel;
        }
        return result;
    }
    /**
     * Gets the JAQL representation of this instance
     *
     * @param nested - defines whether the JAQL is nested within parent JAQL statement or a root JAQL element
     */
    jaql(nested) {
        const result = this[this.defaultLevel].jaql();
        result.jaql.title = this.name;
        return nested ? result.jaql : result;
    }
}
exports.DimensionalDateDimension = DimensionalDateDimension;
/**
 * Creates a new Dimension instance from the given JSON object.
 *
 * This function is used in the generated data model code to create dimensions for an input data source.
 *
 * See also functions {@link createDateDimension} and {@link createAttribute}.
 *
 * @param json - JSON object representing the Dimension
 * @returns A new Dimension instance
 * @group Data Model Utilities
 */
function createDimension(json) {
    const name = json.name || json.title;
    const description = json.desc || json.description;
    const expression = json.expression || json.dim;
    const type = DimensionalDimension.parseType(json.dimtype || json.type);
    const sort = json.sort;
    const dataSource = json.dataSource;
    // date dimension
    if (type == types_js_1.MetadataTypes.DateDimension) {
        return new DimensionalDateDimension(name, expression, description, sort, dataSource);
    }
    // attributes
    const atts = Object.getOwnPropertyNames(json)
        .map((p) => json[p])
        .filter((v) => types_js_1.MetadataTypes.isAttribute(v.type));
    if (atts.length == 0) {
        if (json.attributes) {
            let att;
            for (let i = 0; i < json.attributes.length; i++) {
                att = json.attributes[i];
                atts.push(new attributes_js_1.DimensionalAttribute(att.name, att.expression, att.type, att.description, att.sort, att.dataSource));
            }
        }
        // default attribute
        else if (expression) {
            atts.push(new attributes_js_1.DimensionalAttribute(name, expression, type, description, sort, dataSource));
        }
    }
    // nested dimensions
    const dims = Object.getOwnPropertyNames(json)
        .map((p) => json[p])
        .filter((v) => types_js_1.MetadataTypes.isDimension(v.type));
    if (dims.length == 0 && json.dimensions) {
        for (let i = 0; i < json.dimensions.length; i++) {
            dims.push(createDimension(json.dimensions[i]));
        }
    }
    const d = new DimensionalDimension(name, expression, atts, dims, type, description, sort, dataSource);
    if (json.defaultAttribute) {
        d.defaultAttribute = atts.find((a) => a.name === json.defaultAttribute);
    }
    return d;
}
exports.createDimension = createDimension;
/**
 * Creates a new Date Dimension instance from the given JSON object.
 *
 * This function is used in the generated data model code to create date dimensions for an input data source.
 *
 * See also functions {@link createDimension} and {@link createAttribute}.
 *
 * @param json - JSON object representing the Date Dimension
 * @returns A new Date Dimension instance
 * @group Data Model Utilities
 */
function createDateDimension(json) {
    const name = json.name || json.title;
    const expression = json.expression || json.dim;
    const description = json.desc || json.description;
    const sort = json.sort;
    const dataSource = json.dataSource;
    return new DimensionalDateDimension(name, expression, description, sort, dataSource);
}
exports.createDateDimension = createDateDimension;
